import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  TrendingUp, RefreshCw, Loader2, Users, Briefcase, IndianRupee, LineChart,
  Sparkles, MapPin, AlertCircle, Gauge, Building2, Award,
} from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { cn } from '@/lib/utils';
import { getMarketInsights } from './trendsApi';

// Survives tab switches / remounts within the session (same pattern as
// RecommendedCandidates) so returning to the Trends tab is instant, no refetch.
const marketCache = new Map(); // jobId -> data

// Confidence labels only — we deliberately do NOT show data-source names here.
const CONF_META = {
  high: { label: 'Measured', cls: 'bg-emerald-100 text-emerald-700' },
  medium: { label: 'Estimate', cls: 'bg-amber-100 text-amber-700' },
  low: { label: 'Rough est.', cls: 'bg-slate-100 text-slate-500' },
};

// First available metric among the given keys (falls back to first existing).
const pick = (metrics, ...keys) => {
  for (const k of keys) {
    const m = (metrics || []).find((x) => x.key === k && x.available);
    if (m) return m;
  }
  for (const k of keys) {
    const m = (metrics || []).find((x) => x.key === k);
    if (m) return m;
  }
  return null;
};

function ConfBadge({ confidence }) {
  const meta = CONF_META[confidence] || CONF_META.medium;
  return <Badge variant="secondary" className={cn('h-5 px-1.5 text-[10px]', meta.cls)}>{meta.label}</Badge>;
}

function Sparkline({ series }) {
  const pts = (series || []).map((p) => p && p.value).filter((v) => typeof v === 'number');
  if (pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts), range = max - min || 1;
  const w = 140, h = 32, step = w / (pts.length - 1);
  const d = pts
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  const up = pts[pts.length - 1] >= pts[0];
  return (
    <svg width={w} height={h} className={up ? 'text-emerald-500' : 'text-rose-500'} aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function StatCard({ icon: Icon, title, metric, children }) {
  const available = metric && metric.available;
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium">{title}</span>
      </div>
      {available ? (
        <>
          <div className="mt-1.5 flex items-end justify-between gap-2">
            <div className="text-2xl font-bold leading-tight text-slate-900">
              {metric.display ?? String(metric.value)}
            </div>
            {children}
          </div>
          {metric.unit ? <div className="text-[11px] text-slate-400">{metric.unit}</div> : null}
          <div className="mt-2 flex flex-wrap items-center gap-1">
            <ConfBadge confidence={metric.confidence} />
          </div>
          {metric.method ? (
            <div className="mt-1.5 text-[11px] leading-snug text-slate-400">{metric.method}</div>
          ) : null}
        </>
      ) : (
        <div className="mt-2 flex-1">
          <div className="text-sm font-medium text-slate-400">Not available</div>
          {metric?.method ? (
            <div className="mt-1 text-[11px] leading-snug text-slate-400">{metric.method}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Market Trends panel (the "Trends" tab). External talent-market intelligence
 * for the role — people available, demand, salary, and trend. Figures carry a
 * confidence label; data-source names are intentionally not shown.
 */
const MarketTrends = ({ jobId }) => {
  const cached = marketCache.get(jobId);
  const [data, setData] = useState(cached || null);
  const [isLoading, setIsLoading] = useState(!cached);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setIsRefreshing(true); else if (!marketCache.get(jobId)) setIsLoading(true);
    setError('');
    try {
      const res = await getMarketInsights(jobId, { refresh });
      if (mountedRef.current) setData(res);
      marketCache.set(jobId, res);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not load market insights.';
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current) { setIsLoading(false); setIsRefreshing(false); }
    }
  }, [jobId]);

  // Hydrate instantly from the session cache; only fetch the first time.
  useEffect(() => {
    const c = marketCache.get(jobId);
    if (c) { setData(c); setIsLoading(false); return; }
    load();
  }, [jobId, load]);

  const metrics = data?.metrics || [];
  const analysis = data?.analysis || null;

  const available = pick(metrics, 'supply_verified', 'open_to_work', 'supply_web', 'supply_dev');
  const demand = pick(metrics, 'demand');
  const salary = pick(metrics, 'salary');
  const trendM = pick(metrics, 'trend');
  const hotspots = pick(metrics, 'hotspots');
  const supplyVerified = metrics.find((m) => m.key === 'supply_verified' && m.available);
  const supplyDev = metrics.find((m) => m.key === 'supply_dev' && m.available);
  const supplyWeb = metrics.find((m) => m.key === 'supply_web' && m.available);
  const openToWorkM = metrics.find((m) => m.key === 'open_to_work' && m.available);
  const supplyRows = [supplyVerified, openToWorkM, supplyWeb, supplyDev].filter(Boolean);
  const interestM = metrics.find((m) => m.key === 'interest' && m.available);
  const tightness = metrics.find((m) => m.key === 'market_tightness' && m.available);
  const skillsDemand = metrics.find((m) => m.key === 'skills_demand' && m.available);
  const employers = metrics.find((m) => m.key === 'top_employers' && m.available);
  const verdict = analysis?.supply_demand_verdict;

  return (
    <Card className="border-2 border-primary/20 bg-primary/[0.02]">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <Heading level="h3" className="text-base font-semibold">Market intelligence</Heading>
              <Text size="xs" variant="muted">
                The external talent market for this role, with how much to trust each figure.
              </Text>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => load({ refresh: true })}
                  disabled={isLoading || isRefreshing} className="h-8 gap-1.5" title="Re-run market research">
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <Loader2 className="mb-3 h-6 w-6 animate-spin text-primary" />
            <Text size="sm" className="font-medium">Researching the market…</Text>
            <Text size="xs" variant="muted">Pulling demand, salary, supply and open-to-work signals.</Text>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        ) : (
          <>
            {/* Hero band */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard icon={Users} title="People available / open to work" metric={available} />
              <StatCard icon={Briefcase} title="Market demand (open roles)" metric={demand} />
              <StatCard icon={IndianRupee} title="Salary benchmark" metric={salary} />
              <StatCard icon={LineChart} title="Salary trend (12 mo)" metric={trendM}>
                {trendM?.available && trendM.value?.series ? <Sparkline series={trendM.value.series} /> : null}
              </StatCard>
              {interestM ? (
                <StatCard icon={TrendingUp} title="Search interest (12 mo)" metric={interestM}>
                  {interestM.value?.series ? <Sparkline series={interestM.value.series} /> : null}
                </StatCard>
              ) : null}
            </div>

            {/* Supply–demand headline — the hiring-difficulty read */}
            {tightness ? (
              <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                <Gauge className="h-6 w-6 shrink-0 text-primary" />
                <div>
                  <div className="text-lg font-bold leading-tight text-slate-900">{tightness.display}</div>
                  <div className="text-xs text-slate-500">
                    {tightness.unit}{tightness.method ? ` · ${tightness.method}` : ''}
                  </div>
                </div>
              </div>
            ) : null}

            {/* AI analysis — grounded on the measured numbers only */}
            {analysis?.summary ? (
              <div className="rounded-xl border border-primary/20 bg-white p-3.5">
                <div className="flex items-center gap-1.5 text-primary">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">AI analysis of the measured data</span>
                  {verdict ? (
                    <Badge variant="secondary" className="ml-auto bg-primary/10 text-primary text-[11px] capitalize">
                      {verdict}
                    </Badge>
                  ) : null}
                </div>
                <Text size="sm" className="mt-2 text-slate-700">{analysis.summary}</Text>
                {analysis.recommendation ? (
                  <div className="mt-2 text-sm text-slate-800">
                    <span className="font-medium">Recommendation: </span>{analysis.recommendation}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Detail: supply breakdown + hotspots */}
            {(supplyRows.length || hotspots?.available) ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {supplyRows.length ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-2 text-xs font-medium text-slate-500">Talent supply</div>
                    <div className="space-y-2">
                      {supplyRows.map((m) => (
                        <div key={m.key} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-slate-700">{m.label}</span>
                          <span className="text-sm font-semibold">{m.display}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {hotspots?.available ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-2 flex items-center gap-1 text-xs font-medium text-slate-500">
                      <MapPin className="h-3.5 w-3.5" /> Where the roles are
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(hotspots.value || []).slice(0, 6).map((r, i) => (
                        <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                          {r.location}{typeof r.count === 'number' ? ` · ${r.count.toLocaleString()}` : ''}
                          {r.average_salary ? ` · ₹${Math.round(r.average_salary / 100000)}L` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Skills employers want + top companies hiring (mined from live ads) */}
            {(skillsDemand || employers) ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {skillsDemand ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-2 flex items-center gap-1 text-xs font-medium text-slate-500">
                      <Award className="h-3.5 w-3.5" /> Skills employers want
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(skillsDemand.value || []).slice(0, 10).map((s, i) => (
                        <span key={i} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">{s}</span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {employers ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-2 flex items-center gap-1 text-xs font-medium text-slate-500">
                      <Building2 className="h-3.5 w-3.5" /> Top companies hiring
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(employers.value || []).slice(0, 8).map((c, i) => (
                        <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                          {c.company}{typeof c.count === 'number' ? ` · ${c.count}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Footer: honesty disclaimer (no source names) */}
            <div className="flex flex-col gap-1 pt-1">
              {data?.cached ? (
                <Text size="xs" variant="muted" className="text-center">Cached result</Text>
              ) : null}
              <Text size="xs" variant="muted" className="text-center text-[10px]">
                Availability figures are indexed estimates, not verified counts. Demand, salary and trend are live market data.
              </Text>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketTrends;
